import { bbox, bboxPolygon, feature, featureCollection } from '@turf/turf';
import { Feature, Geometry } from 'geojson';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPublicSavedSelection } from '@/endpoints/itasca/land/saved-selections/getPublicSavedSelection';
import { getSharedSelections } from '@/endpoints/itasca/land/share/getSharedSelection';
import { LayerNames } from '@/models/embed/layers';
import { AcresFeature } from '@/models/features';
import { SavedSelection } from '@/models/land';
import { multiUnion } from '@/utils/multiUnion';

const makeZoomBounds = (geometries: Geometry[]) => {
  const theBox = bbox(featureCollection(geometries.map((x) => feature(x))));
  return bboxPolygon(theBox);
};

export type EmbedSourceData = {
  /** The boundary of the land that is being shown in the embed */
  boundary: AcresFeature | null;
  zoomBoundary: Feature<Geometry> | null;
  /** IDK what this is  */
  originalBoundary: AcresFeature[] | null;
  /** The layers that are to be shown in the embed */
  layers: LayerNames[];
  sharecode: string | null;
  savedSelection: SavedSelection | null;
};

/**
 * Hook for managing the source of the data for the embed
 * Returns the embed data depending on what was provided as a source (sharecode or savedselection)
 */
export const useEmbedSource = (): EmbedSourceData => {
  const [boundary, setBoundary] = useState<AcresFeature | null>(null);
  const [zoomBoundary, setZoomBoundary] = useState<AcresFeature | null>(null);
  const [originalBoundary, setOriginalBoundary] = useState<
    AcresFeature[] | null
  >(null);

  const [savedSelection, setSavedSelection] = useState<SavedSelection | null>(
    null
  );
  const query = useSearchParams();

  const sharecode: string | null = useMemo(() => {
    const id = query?.get('sharecode');
    if (id && typeof id === 'string') return id;

    return null;
  }, [query]);

  const savedId: number | null = useMemo(() => {
    const id = query?.get('savedId');
    if (id && typeof id === 'string') return +id;

    return null;
  }, [query]);

  const layers: LayerNames[] = useMemo(() => {
    const lys = query?.get('layers');
    const layersArray: LayerNames[] = [];
    if (lys && typeof lys === 'string') {
      layersArray.push(...(lys.split(',') as LayerNames[]));
    }

    return layersArray;
  }, [query]);

  const fetchSavedSelection = useCallback(async (id: number) => {
    const { res } = await getPublicSavedSelection(id);

    if (res) {
      const boundariesUnion = multiUnion(res.data.boundaries as Feature[]);
      setSavedSelection(res.data);
      setBoundary(boundariesUnion);
      setOriginalBoundary(res.data.boundaries);

      // Make a bounding box including customizations if there are customizations
      if (res.data.customizations?.length) {
        setZoomBoundary(
          makeZoomBounds([
            ...res.data.boundaries.map((x) => x.geometry),
            ...(res.data.customizations?.map((x) => x.geometry) ?? []),
          ])
        );
      } else {
        setZoomBoundary(boundariesUnion);
      }
    }
  }, []);

  const fetchShareCode = useCallback(async (code: string) => {
    const { res, err } = await getSharedSelections(code);

    if (err) {
      return;
    }

    if (res) {
      const boundaryUnion = multiUnion(res.data.boundary_set);
      setBoundary(boundaryUnion);
      setZoomBoundary(boundaryUnion);
      setOriginalBoundary(res.data.boundary_set);
    }
  }, []);

  useEffect(() => {
    if (sharecode) {
      fetchShareCode(sharecode);
      return;
    }

    if (savedId) {
      fetchSavedSelection(savedId);
      return;
    }

    throw Error('No valid share code or saved selection id was provided');
  }, [fetchShareCode, fetchSavedSelection, sharecode, savedId]);

  return {
    boundary,
    layers,
    originalBoundary,
    savedSelection,
    sharecode,
    zoomBoundary,
  };
};
